// --------------------------------------------------------
// REGISTER COMPANY FORM - RegisterCompanyForm
// --------------------------------------------------------

// --------------------------------------------------------
// GENERAL
// --------------------------------------------------------
//import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select } from '@mui/material';
import {
  FormControl,
  InputLabel,
  MenuItem,
  ListItemText,
  Checkbox,
  Select,
  FormControlLabel,
} from "@mui/material";
import { Form, useFormikContext } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Colors from "../../constants/Colors";
import { Paths } from "../../enums/Paths";
import { checkObjectEquality } from "../../helpers/HelperFunctions";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import Unit from "../../models/core/unit/Unit";
import {
  Container,
  FooterButtonArea,
  RowContainer,
} from "../../pages/company/companyRegister/CompanyRegister.styled";
import GenericModal from "../genericModal/GenericModal";
import { GenericSolidButton } from "../UI/buttons/Button.styled";
import CancelOrEditButton from "../UI/buttons/CancelOrEditButton";
import { H2, TextLink, Text, H3 } from "../UI/generalLayout/Layout.styled";
import ScreenHeader from "../UI/screenHeader/ScreenHeader";
import BackButton from "../UI/buttons/BackButton";
import { FormContainer } from "./Form.styled";
import FormikTextField from "./FormikTextField";
import RightIcon from "./RightIcon";
import * as companyActions from "../../store/actions/company";
import * as unitActions from "../../store/actions/unit";
import { useAppDispatch } from "../..";
import BaseResponse from "../../models/core/BaseResponse";
import { UnitService } from "../../core/http/service/UnitService";

// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
// --------------------------------------------------------

// --------------------------------------------------------
// REGISTER COMPANY FORM INTERFACE
// --------------------------------------------------------

interface IRegisterCompanyForm {
  isConfirmationState: boolean;
  isEditState: boolean;
  isViewState: boolean;
  initialValues: any;
  formValues: any;
  formErrors: any;
  errors: any;
  formTouched: any;
  unidadesAtendimento: any;
  onClickCancel: () => void;
  onClickConfirmar: () => void;
  onCloseError: () => void;
}

/**
/**
 * A component that renders a Component description here.
 *
 * @param props The component props.
 * @returns The component JSX.
 */
const RegisterCompanyForm = ({
  isConfirmationState,
  isEditState,
  isViewState,
  formValues,
  formErrors,
  formTouched,
  errors,
  unidadesAtendimento,
  initialValues,
  onClickCancel,
  onClickConfirmar,
  onCloseError,
}: IRegisterCompanyForm) => {
  // --------------------------------------------------------
  // REGISTER COMPANY FORM PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const dispatch = useAppDispatch();

  const units: Unit[] = useSelector((state: any) => state.unit.units);

  const fetchUnits = useCallback(async () => {
    await dispatch(unitActions.fetchUnitList());
  }, []);

  useEffect(() => {
    if (units.length === 0) {
      fetchUnits();
    }
  }, [units]);

  const { values, setFieldValue } = useFormikContext();
  const [lastUnidades, setLastUnidades] = useState<any>([]);
  const [unidades, setUnidades] = useState<any>([]);
  const [idsUnidades] = useState<any>([]);

  const [isFormEmpty, setFormEmpty] = useState(true);
  const [showModalVoltar, setShowModalVoltar] = useState(false);

  const [noFieldEditedOnEditState, setNoFieldEditedOnEditState] =
    useState(isEditState);
  const { isMobile } = useResponsiveInfo();

  const navigate = useNavigate();
  const confirmButtonLabel = isConfirmationState
    ? "Finalizar cadastro"
    : "Confirmar cadastro";
  const screenLabel = isViewState
    ? "Visualizar empresa"
    : isEditState
      ? "Editar empresa"
      : "Cadastro de Empresa";

  useEffect(() => {
    const valueList = Object.values(formValues).filter((value) => {
      return value !== "";
    });
    if (valueList.length > 0) {
      setFormEmpty(false);
    } else {
      setFormEmpty(true);
    }

    if (isEditState) {
      if (
        !checkObjectEquality(initialValues, formValues) ||
        !checkObjectEquality(lastUnidades, unidades)
      ) {
        setNoFieldEditedOnEditState(false);
      }
    }
  }, [formValues, unidades]);

  const filterUnits = useCallback(async () => {
    const unidades: any[] = [];

    for (const unitId of unidadesAtendimento) {
      const service: UnitService = new UnitService();
      const response: any = await service.getUnitById(unitId);

      const baseResponse: BaseResponse<any> = new BaseResponse().fromJSON(
        response.data
      );

      unidades.push(baseResponse.data.nomeFantasia);
    }

    setUnidades(unidades);
    setLastUnidades(unidades);
  }, [unidadesAtendimento]);

  useEffect(() => {
    filterUnits();
  }, [unidadesAtendimento]);

  const navigateToCompanyList = () => {
    return navigate(Paths.CompanyList);
  };

  const onVoltar = () => {
    if (isFormEmpty || noFieldEditedOnEditState) {
      navigateToCompanyList();
    } else {
      setShowModalVoltar(true);
    }
  };

  const onCancelOrEdit = () => {
    if (isFormEmpty || noFieldEditedOnEditState) {
      navigateToCompanyList();
    } else {
      onClickCancel();
    }
  };

  async function arrayUnidades() {
    let ids: any = [];

    units.forEach((r: any) => {
      unidades.forEach((r2: any) => {
        idsUnidades.slice(0, idsUnidades.length);

        if (r.nomeFantasia === r2) {
          ids.push(r.id);
        }
      });
    });
    await dispatch(companyActions.listIdsUnids(ids));
  }

  useEffect(() => {
    arrayUnidades();
  }, [units, unidades]);

  // --------------------------------------------------------

  // --------------------------------------------------------
  // REACT RETURN FUNCTION
  // --------------------------------------------------------
  return (
    <>
      <Form>
        <FormContainer isConfirmationState={isConfirmationState}>
          <Container>
            <FormikTextField
              variant="standard"
              label="Nome da empresa"
              name="razaoSocial"
              disabled={isConfirmationState || isViewState}
              error={formErrors.razaoSocial !== undefined}
              helperText={
                formErrors.razaoSocial !== undefined
                  ? formErrors.razaoSocial
                  : ""
              }
              inputProps={{ maxLength: 150 }}
              InputProps={{
                endAdornment: (
                  <RightIcon
                    value={formValues.razaoSocial}
                    hasError={formErrors.razaoSocial && formTouched.razaoSocial}
                  />
                ),
              }}
              sx={{ width: isMobile ? "100%" : "49%" }}
            />
          </Container>
          <Container>
            <FormikTextField
              variant="standard"
              label="Nome Fantasia"
              name="nomeFantasia"
              disabled={isConfirmationState || isViewState}
              error={formErrors.nomeFantasia !== undefined}
              helperText={
                formErrors.nomeFantasia !== undefined
                  ? formErrors.nomeFantasia
                  : ""
              }
              inputProps={{ maxLength: 150 }}
              InputProps={{
                endAdornment: (
                  <RightIcon
                    value={formValues.nomeFantasia}
                    hasError={formErrors.nomeFantasia}
                  />
                ),
              }}
              sx={{ width: isMobile ? "100%" : "49%" }}
            />
          </Container>
          <Container>
            <FormikTextField
              variant="standard"
              name="cnpj"
              label="CNPJ"
              mask="99.999.999/9999-99"
              error={formErrors.cnpj !== undefined}
              helperText={formErrors.cnpj !== undefined ? formErrors.cnpj : ""}
              disabled={isConfirmationState || isViewState}
              InputProps={{
                endAdornment: (
                  <RightIcon
                    value={formValues.cnpj}
                    hasError={formErrors.cnpj}
                  />
                ),
              }}
              sx={{ width: isMobile ? "100%" : "49%" }}
            />
          </Container>
          <Container>
            <FormikTextField
              variant="standard"
              name="telefoneEmpresa"
              label="Telefone da empresa"
              mask="(99) 99999-9999"
              error={formErrors.telefoneEmpresa !== undefined}
              helperText={
                formErrors.telefoneEmpresa !== undefined
                  ? formErrors.telefoneEmpresa
                  : ""
              }
              disabled={isConfirmationState || isViewState}
              InputProps={{
                endAdornment: (
                  <RightIcon
                    value={formValues.telefoneEmpresa}
                    hasError={formErrors.telefoneEmpresa}
                  />
                ),
              }}
              sx={{ width: isMobile ? "100%" : "49%" }}
            />
          </Container>
          <div style={{ marginTop: 32 }}>
            <H2>Endereço da empresa</H2>
            <Container>
              <FormikTextField
                variant="standard"
                name="cep"
                label="CEP"
                mask="99.999-999"
                error={formErrors.cep !== undefined}
                helperText={formErrors.cep !== undefined ? formErrors.cep : ""}
                disabled={isConfirmationState || isViewState}
                InputProps={{
                  endAdornment: (
                    <RightIcon
                      value={formValues.cep}
                      hasError={formErrors.cep}
                    />
                  ),
                }}
                sx={{ width: isMobile ? "100%" : "49%" }}
              />
            </Container>
            <RowContainer>
              <FormikTextField
                variant="standard"
                label="Endereço"
                name="logradouro"
                inputProps={{ maxLength: 200 }}
                disabled={isConfirmationState || isViewState}
                error={formErrors.logradouro !== undefined}
                helperText={
                  formErrors.logradouro !== undefined
                    ? formErrors.logradouro
                    : ""
                }
                InputProps={{
                  endAdornment: (
                    <RightIcon
                      value={formValues.logradouro}
                      hasError={formErrors.logradouro}
                    />
                  ),
                }}
                sx={{ width: isMobile ? "100%" : "49%", marginRight: 2 }}
              />
              <FormikTextField
                variant="standard"
                label="Número"
                name="numero"
                disabled={isConfirmationState || isViewState}
                error={formErrors.numero !== undefined}
                helperText={
                  formErrors.numero !== undefined ? formErrors.numero : ""
                }
                inputProps={{ maxLength: 20 }}
                InputProps={{
                  endAdornment: (
                    <RightIcon
                      value={formValues.numero}
                      hasError={formErrors.numero}
                    />
                  ),
                }}
                sx={{ width: isMobile ? "100%" : "49%" }}
              />
            </RowContainer>
            <RowContainer>
              <FormikTextField
                variant="standard"
                label="Complemento"
                name="complemento"
                inputProps={{ maxLength: 50 }}
                disabled={isConfirmationState || isViewState}
                error={formErrors.complemento !== undefined}
                helperText={
                  formErrors.complemento !== undefined
                    ? formErrors.complemento
                    : ""
                }
                InputProps={{
                  endAdornment: (
                    <RightIcon
                      value={formValues.complemento}
                      hasError={formErrors.complemento}
                    />
                  ),
                }}
                sx={{ width: isMobile ? "100%" : "49%", marginRight: 2 }}
              />
              <FormikTextField
                variant="standard"
                label="Bairro"
                name="bairro"
                disabled={isConfirmationState || isViewState}
                error={formErrors.bairro !== undefined}
                helperText={
                  formErrors.bairro !== undefined ? formErrors.bairro : ""
                }
                inputProps={{ maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <RightIcon
                      value={formValues.bairro}
                      hasError={formErrors.bairro}
                    />
                  ),
                }}
                sx={{ width: isMobile ? "100%" : "49%" }}
              />
            </RowContainer>
            <RowContainer>
              <FormikTextField
                variant="standard"
                label="Estado"
                name="estado"
                disabled={isConfirmationState || isViewState}
                error={formErrors.estado !== undefined}
                helperText={
                  formErrors.estado !== undefined ? formErrors.estado : ""
                }
                inputProps={{ maxLength: 2 }}
                InputProps={{
                  endAdornment: (
                    <RightIcon
                      value={formValues.estado}
                      hasError={formErrors.estado}
                    />
                  ),
                }}
                sx={{ width: isMobile ? "100%" : "49%", marginRight: 2 }}
              />
              <FormikTextField
                variant="standard"
                label="Cidade"
                name="cidade"
                disabled={isConfirmationState || isViewState}
                error={formErrors.cidade !== undefined}
                helperText={
                  formErrors.cidade !== undefined ? formErrors.cidade : ""
                }
                inputProps={{ maxLength: 200 }}
                InputProps={{
                  endAdornment: (
                    <RightIcon
                      value={formValues.cidade}
                      hasError={formErrors.cidade}
                    />
                  ),
                }}
                sx={{ width: isMobile ? "100%" : "49%" }}
              />
            </RowContainer>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <div
              style={{
                marginTop: 24,
                width: isMobile ? "100%" : "49%",
                marginRight: 16,
              }}
            >
              <H2>Dados do responsável</H2>
              <Container>
                <FormikTextField
                  variant="standard"
                  label="Nome do responsável"
                  name="nomeResponsavel"
                  disabled={isConfirmationState || isViewState}
                  error={formErrors.nomeResponsavel !== undefined}
                  helperText={
                    formErrors.nomeResponsavel !== undefined
                      ? formErrors.nomeResponsavel
                      : ""
                  }
                  inputProps={{ maxLength: 150 }}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.nomeResponsavel}
                        hasError={formErrors.nomeResponsavel}
                      />
                    ),
                  }}
                  sx={{ width: "100%" }}
                />
              </Container>
              <Container>
                <FormikTextField
                  variant="standard"
                  name="cpfResponsavel"
                  label="CPF"
                  mask="999.999.999-99"
                  error={formErrors.cpfResponsavel !== undefined}
                  helperText={
                    formErrors.cpfResponsavel !== undefined
                      ? formErrors.cpfResponsavel
                      : ""
                  }
                  disabled={isConfirmationState || isViewState}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.cpfResponsavel}
                        hasError={formErrors.cpfResponsavel}
                      />
                    ),
                  }}
                  sx={{ width: "100%" }}
                />
              </Container>
              <Container>
                <FormikTextField
                  variant="standard"
                  label="E-mail"
                  name="emailResponsavel"
                  disabled={isConfirmationState || isViewState}
                  error={formErrors.emailResponsavel !== undefined}
                  helperText={
                    formErrors.emailResponsavel !== undefined
                      ? formErrors.emailResponsavel
                      : ""
                  }
                  inputProps={{ maxLength: 50 }}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.emailResponsavel}
                        hasError={formErrors.emailResponsavel}
                      />
                    ),
                  }}
                  sx={{ width: "100%" }}
                />
              </Container>
              <Container>
                <FormikTextField
                  variant="standard"
                  name="telefoneResponsavel"
                  label="Telefone para contato"
                  mask="(99) 99999-9999"
                  error={formErrors.telefoneResponsavel !== undefined}
                  helperText={
                    formErrors.telefoneResponsavel !== undefined
                      ? formErrors.telefoneResponsavel
                      : ""
                  }
                  disabled={isConfirmationState || isViewState}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.telefoneResponsavel}
                        hasError={formErrors.telefoneResponsavel}
                      />
                    ),
                  }}
                  sx={{ width: "100%" }}
                />
              </Container>
              <Container>
                <FormikTextField
                  variant="standard"
                  name="telefoneResponsavel2"
                  label="Telefone para contato (2)"
                  mask="(99) 99999-9999"
                  error={
                    formErrors.telefoneResponsavel2 !== undefined &&
                    formValues.telefoneResponsavel2
                  }
                  helperText={
                    formErrors.telefoneResponsavel2 !== undefined &&
                      formValues.telefoneResponsavel2
                      ? formErrors.telefoneResponsavel2
                      : ""
                  }
                  disabled={isConfirmationState || isViewState}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.telefoneResponsavel2}
                        hasError={formErrors.telefoneResponsavel2}
                        isOptional
                      />
                    ),
                  }}
                  sx={{ width: "100%" }}
                />
              </Container>
            </div>
            <div style={{ marginTop: 24, width: isMobile ? "100%" : "50%" }}>
              <H2>Dados do administrador do sistema</H2>
              <Container>
                <FormikTextField
                  variant="standard"
                  label="Nome do administrador"
                  name="nomeAdministrador"
                  disabled={isConfirmationState || isViewState}
                  error={formErrors.nomeAdministrador !== undefined}
                  helperText={
                    formErrors.nomeAdministrador !== undefined
                      ? formErrors.nomeAdministrador
                      : ""
                  }
                  inputProps={{ maxLength: 150 }}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.nomeAdministrador}
                        hasError={formErrors.nomeAdministrador}
                      />
                    ),
                  }}
                  sx={{ width: "98%" }}
                />
              </Container>
              <Container>
                <FormikTextField
                  variant="standard"
                  name="cpfAdministrador"
                  label="CPF"
                  mask="999.999.999-99"
                  error={formErrors.cpfAdministrador !== undefined}
                  helperText={
                    formErrors.cpfAdministrador !== undefined
                      ? formErrors.cpfAdministrador
                      : ""
                  }
                  disabled={isConfirmationState || isViewState}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.cpfAdministrador}
                        hasError={formErrors.cpfAdministrador}
                      />
                    ),
                  }}
                  sx={{ width: "98%" }}
                />
              </Container>
              <Container>
                <FormikTextField
                  variant="standard"
                  label="E-mail"
                  name="emailAdministrador"
                  inputProps={{ maxLength: 50 }}
                  disabled={isConfirmationState || isViewState}
                  error={formErrors.emailAdministrador !== undefined}
                  helperText={
                    formErrors.emailAdministrador !== undefined
                      ? formErrors.emailAdministrador
                      : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.emailAdministrador}
                        hasError={formErrors.emailAdministrador}
                      />
                    ),
                  }}
                  sx={{ width: "98%" }}
                />
              </Container>
              <Container>
                <FormikTextField
                  variant="standard"
                  name="telefoneAdministrador"
                  label="Telefone para contato"
                  mask="(99) 99999-9999"
                  error={formErrors.telefoneAdministrador !== undefined}
                  helperText={
                    formErrors.telefoneAdministrador !== undefined
                      ? formErrors.telefoneAdministrador
                      : ""
                  }
                  disabled={isConfirmationState || isViewState}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.telefoneAdministrador}
                        hasError={formErrors.telefoneAdministrador}
                      />
                    ),
                  }}
                  sx={{ width: "98%" }}
                />
              </Container>
              <Container>
                <FormikTextField
                  variant="standard"
                  name="telefoneAdministrador2"
                  label="Telefone para contato (2)"
                  mask="(99) 99999-9999"
                  error={
                    formErrors.telefoneAdministrador2 !== undefined &&
                    formValues.telefoneAdministrador2
                  }
                  helperText={
                    formErrors.telefoneAdministrador2 !== undefined &&
                      formValues.telefoneAdministrador2
                      ? formErrors.telefoneAdministrador2
                      : ""
                  }
                  disabled={isConfirmationState || isViewState}
                  InputProps={{
                    endAdornment: (
                      <RightIcon
                        value={formValues.telefoneAdministrador2}
                        hasError={formErrors.telefoneAdministrador2}
                        isOptional
                      />
                    ),
                  }}
                  sx={{ width: "98%" }}
                />
              </Container>
            </div>
          </div>
          <div style={{ marginTop: 26 }}>
            <H2>Unidade de Atendimento</H2>
            {!isViewState ? (
              <FormControl style={{ width: isMobile ? "100%" : "49%" }}>
                <InputLabel style={{ color: Colors.SPM600 }}>
                  Escolha a unidade de atendimento
                </InputLabel>
                <Select
                  disabled={isConfirmationState || isViewState}
                  multiple
                  style={{
                    backgroundColor: "#cfe7fc",
                    borderRadius: 8,
                    color: Colors.SPM600,
                  }}
                  id="demo-simple-select"
                  value={unidades}
                  label="Escolha a unidade de atendimento"
                  onChange={(e) => {
                    setUnidades(e.target.value ?? []);
                  }}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {units.map((unidade, index) => (
                    <MenuItem
                      key={unidade.id || index}
                      style={{
                        display: unidade.inativo ? "none" : "flex",
                        width: isMobile ? "100%" : "400px",
                      }}
                      value={unidade.nomeFantasia}
                    >
                      <Checkbox
                        checked={unidades.indexOf(unidade.nomeFantasia) > -1}
                      />
                      <ListItemText primary={unidade.nomeFantasia} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                {unidades.map((unit: any, index: number) => {
                  return <Text key={index}>{unit}</Text>;
                })}
              </>
            )}
          </div>
        </FormContainer>
        <FooterButtonArea>
          {!isViewState && (
            <>
              <CancelOrEditButton
                style={{ marginBottom: isMobile ? 12 : 0 }}
                isEdit={isConfirmationState}
                onEditClick={onCancelOrEdit}
                onCancelClick={onCancelOrEdit}
              />
              <GenericSolidButton
                color={Colors.SPM600}
                disabled={isFormEmpty || noFieldEditedOnEditState}
                type="submit"
                onClick={onClickConfirmar}
              >
                {confirmButtonLabel}
              </GenericSolidButton>
            </>
          )}
        </FooterButtonArea>
      </Form>
      <GenericModal
        visible={showModalVoltar}
        onCancel={navigateToCompanyList}
        onConfirm={() => {
          setShowModalVoltar(false);
        }}
        modalTitle={"Tem certeza que deseja voltar para a listagem de empresa?"}
        modalSubtitle={"Todos os dados preenchidos serão perdidos."}
        cancelButtonMsg={"Continuar"}
        confirmButtonMsg={"Voltar"}
      />
    </>
  );
  // --------------------------------------------------------
};

export default RegisterCompanyForm;
